import type { CorrectionLetterReasonEntry, ReasonSelection } from "@credit-acceptance/step6-bff-client";
import { toast } from "@credit-acceptance/uikit";
import { clsx } from "clsx";
import { Loader2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../../components/ui/dialog";
import type { FinancialAccessCheck } from "../../../../hooks/useFinancialAccessGuard";
import { getErrorMessage, isHttpError } from "../../../../lib/query/errors";
import { useCorrectionLetter } from "./api/useCorrectionLetter";
import { useSaveCorrectionLetter } from "./api/useSaveCorrectionLetter";
import { LOCKED_CORRECTION_REASON_CODES } from "./correctionLetter.constants";

interface CorrectionLetterModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalId: string;
  readOnly: boolean;
  /** Re-evaluates Financial access (spec X2); called before Apply saves. */
  checkAccess?: () => Promise<FinancialAccessCheck>;
}

const byDisplayOrder = (a: CorrectionLetterReasonEntry, b: CorrectionLetterReasonEntry) =>
  (a.displayOrder ?? 0) - (b.displayOrder ?? 0);

interface CorrectionLetterReasonsProperties {
  approvalId: string;
  reasons: CorrectionLetterReasonEntry[];
  onSaved: () => void;
  readOnly: boolean;
  checkAccess?: () => Promise<FinancialAccessCheck>;
}

// Mounted only once the GET resolves, so the checkbox state can be seeded
// straight from the response. The dialog unmounts its content on close, so
// reopening re-seeds from the refetched server state.
function CorrectionLetterReasons({
  approvalId,
  reasons,
  onSaved,
  readOnly,
  checkAccess,
}: CorrectionLetterReasonsProperties) {
  const { t } = useTranslation();
  const sorted = [...reasons].sort(byDisplayOrder);
  const selectableReasons = sorted.filter((reason) => !reason.thirdPartyAncillary);
  const thirdPartyReasons = sorted.filter((reason) => reason.thirdPartyAncillary);

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(selectableReasons.flatMap((reason) => (reason.selected && reason.code ? [reason.code] : [])))
  );

  const saveCorrectionLetter = useSaveCorrectionLetter(approvalId);
  const [accessChanged, setAccessChanged] = useState(false);

  const toggleReason = (code: string, checked: boolean) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(code);
      } else {
        next.delete(code);
      }
      return next;
    });
  };

  // Sends the desired state for every selectable reason (checked and
  // unchecked), so deselecting a previously saved reason clears it server-side.
  const handleApply = async () => {
    setAccessChanged(false);
    if (checkAccess) {
      const access = await checkAccess();
      if (!access.editable) {
        setAccessChanged(true);
        return;
      }
    }
    const selections: ReasonSelection[] = selectableReasons.flatMap((reason) =>
      reason.code ? [{ code: reason.code, selected: selected.has(reason.code) }] : []
    );
    // mutateAsync, not mutate callbacks: saving rewrites the GET cache, which re-keys
    // (remounts) this component, and mutate() callbacks don't run after an unmount.
    try {
      const saved = await saveCorrectionLetter.mutateAsync(selections);
      if (saved) {
        toast.success(t("financial.correctionLetterModal.saveSuccess"));
      }
      onSaved();
    } catch {
      // Surfaced through `saveCorrectionLetter.isError`; the modal stays open.
    }
  };

  return (
    <>
      <div className="gap-(--spacing-4) p-(--spacing-6) text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) flex flex-col">
        <div className="gap-(--spacing-2) flex flex-col">
          {selectableReasons.map((reason) => {
            const code = reason.code ?? "";
            const disabled = readOnly || LOCKED_CORRECTION_REASON_CODES.has(code);
            return (
              <label
                key={code}
                className={clsx(
                  "gap-(--spacing-3) flex items-center",
                  disabled ? "cursor-not-allowed" : "cursor-pointer"
                )}
              >
                <Checkbox
                  className="bg-white"
                  checked={selected.has(code)}
                  disabled={disabled}
                  onCheckedChange={(checked) => toggleReason(code, checked === true)}
                />
                <span>{reason.description}</span>
              </label>
            );
          })}
        </div>

        {thirdPartyReasons.length > 0 && (
          <div className="gap-(--spacing-2) flex flex-col">
            <p className="font-(--font-weight-semibold)">{t("financial.correctionLetterModal.thirdPartyHeading")}</p>
            {thirdPartyReasons.map((reason) => (
              <label key={reason.code} className="gap-(--spacing-3) flex cursor-not-allowed items-center">
                <Checkbox className="bg-white" checked={reason.selected === true} disabled />
                <span>{reason.description}</span>
              </label>
            ))}
          </div>
        )}

        {sorted.length === 0 && (
          <p className="text-muted-foreground">{t("financial.correctionLetterModal.noReasons")}</p>
        )}
      </div>

      {(accessChanged || (saveCorrectionLetter.isError && isHttpError(saveCorrectionLetter.error, 403))) && (
        <p role="alert" className="text-destructive px-(--spacing-6) pb-(--spacing-2) text-xs">
          {t("financial.accessChanged")}
        </p>
      )}

      {saveCorrectionLetter.isError && !isHttpError(saveCorrectionLetter.error, 403) && (
        <p role="alert" className="text-destructive px-(--spacing-6) pb-(--spacing-2) text-xs">
          {t("financial.correctionLetterModal.saveError")}
        </p>
      )}

      <DialogFooter className="gap-(--spacing-2) px-(--spacing-4) py-(--spacing-2) justify-center border-t sm:justify-center">
        <Button
          type="button"
          size="xs"
          disabled={readOnly || saveCorrectionLetter.isPending}
          onClick={() => void handleApply()}
        >
          {saveCorrectionLetter.isPending && <Loader2 aria-hidden="true" className="size-3 animate-spin" />}
          {t("financial.correctionLetterModal.apply")}
        </Button>
        <DialogClose asChild>
          <Button type="button" size="xs" variant="outline" disabled={saveCorrectionLetter.isPending}>
            {t("financial.correctionLetterModal.cancel")}
          </Button>
        </DialogClose>
      </DialogFooter>
    </>
  );
}

// Opened from the "Correction Letter" link in the Financial section header.
function CorrectionLetterModal({
  open,
  onOpenChange,
  approvalId,
  readOnly,
  checkAccess,
}: CorrectionLetterModalProperties) {
  const { t } = useTranslation();
  const { data, dataUpdatedAt, isLoading, isError, error } = useCorrectionLetter(approvalId, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="gap-(--spacing-0) p-(--spacing-0) overflow-hidden bg-white sm:max-w-lg"
      >
        <div className="bg-primary text-primary-foreground px-(--spacing-4) py-(--spacing-2) flex items-center justify-between">
          <DialogTitle className="text-primary-foreground text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) font-(--font-weight-semibold)">
            {t("financial.correctionLetterModal.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("financial.correctionLetterModal.description")}</DialogDescription>
          <DialogClose className="text-primary-foreground/80 hover:text-primary-foreground p-(--spacing-0-5) transition-colors">
            <X className="size-4.5" aria-hidden="true" />
            <span className="sr-only">{t("financial.correctionLetterModal.close")}</span>
          </DialogClose>
        </div>

        {isLoading && (
          <div className="text-muted-foreground gap-(--spacing-1) p-(--spacing-6) flex items-center text-xs">
            <Loader2 aria-hidden="true" className="size-3 animate-spin" />
            {t("financial.correctionLetterModal.loading")}
          </div>
        )}

        {isError && (
          <p role="alert" className="text-destructive p-(--spacing-6) text-xs">
            {t("financial.correctionLetterModal.loadError", { message: getErrorMessage(error) })}
          </p>
        )}

        {/* Keyed on dataUpdatedAt so a background refetch re-seeds the checkboxes. */}
        {data && (
          <CorrectionLetterReasons
            key={dataUpdatedAt}
            approvalId={approvalId}
            reasons={data.reasons ?? []}
            onSaved={() => onOpenChange(false)}
            readOnly={readOnly}
            checkAccess={checkAccess}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CorrectionLetterModal;
